import type {
    BasicPmGeometry,
    FullWindowGeometry,
    LandscapeDrag,
    LandscapeGeometry,
    MobileTheaterGeometry,
    PortraitGeometry,
    RoomGeometry,
    SplitGeometry,
    TheaterGeometry,
} from '../../types/state'
import APP_BREAKPOINTS from '../components/styles/breakpoints'
import {
    COMPONENT_HEIGHTS,
    MIN_CHAT_WIDTH,
    MIN_VIDEO_WIDTH,
    ROOM_BORDER_WIDTH,
} from '../components/theme'
import { RoomView, ViewMode } from '../constants/enums'
import { PM_LIST_MAX_WIDTH, PM_SPLIT_SPECS } from '../constants/floaters'
import {
    calcHeight,
    calcWidth,
    calcZoomedVideoHeight,
    getPercentage,
} from '../utilities/dimensions'
import { resolveSplit } from '../utilities/splitView'

export type RoomGeometryInputs = {
    roomView: RoomView
    viewMode: ViewMode
    /** the usable room: the whole room, or the content area beside pins */
    width: number
    height: number
    windowWidth: number
    windowHeight: number
    roomX: number
    division: number
    isMobile: boolean
    pmListCollapsed: boolean
}

const { actionsBar, chatHeader } = COMPONENT_HEIGHTS

export const calcPmSplit = ({
    roomView,
    viewMode,
    width,
    height,
    isMobile,
    pmListCollapsed,
}: RoomGeometryInputs): SplitGeometry | null => {
    if (roomView !== RoomView.PM || viewMode !== ViewMode.Normal || isMobile)
        return null
    const split = resolveSplit(width, PM_SPLIT_SPECS, {
        aMaxWidth: PM_LIST_MAX_WIDTH,
        collapseA: pmListCollapsed,
        favorB: true,
    })
    return {
        listWidth: split.canFitBoth ? split.aExpandedPixels : 0,
        roomWidth: split.bPixels,
        layoutWidth: split.originalBWidth,
        vertical: split.originalBWidth < 720 || width <= height,
        canFitList: split.canFitBoth,
    }
}

export const calcLandscapeGeometry = ({
    roomView,
    width,
    height,
    layoutWidth,
    division,
    originX,
}: {
    roomView: RoomView
    width: number
    height: number
    layoutWidth: number
    division: number
    originX: number
}): LandscapeGeometry => {
    const isProfile = roomView === RoomView.Profile
    // the root's border is outside the flex columns and their height budget
    const innerWidth = Math.max(width - ROOM_BORDER_WIDTH * 2, 0)
    const chatSpace = Math.max(height - ROOM_BORDER_WIDTH * 2, 0)
    const availableHeight = Math.max(chatSpace - chatHeader - actionsBar, 0)
    const fittedWidth = calcWidth(availableHeight)
    // in a PM split the video keeps its uncapped column's share, so width
    // recovered by capping the list goes to chat
    const basisWidth = width > 0 ? (innerWidth * layoutWidth) / width : 0
    const maxVideoWidth = Math.min(innerWidth - MIN_CHAT_WIDTH, fittedWidth)
    const videoWidth = isProfile
        ? Math.min(innerWidth / 2, fittedWidth)
        : Math.max(
              Math.min(
                  Math.max((basisWidth * division) / 100, MIN_VIDEO_WIDTH),
                  maxVideoWidth,
              ),
              0,
          )
    const videoHeight = calcHeight(videoWidth)
    const videoColumnWidth = isProfile ? innerWidth / 2 : videoWidth
    const avatarHeight = innerWidth / 2

    return {
        videoColumnWidth,
        chatColumnWidth: innerWidth - videoColumnWidth,
        videoWidth,
        videoHeight,
        avatarHeight,
        heightBelowVideo: {
            video: Math.max(availableHeight - videoHeight, 0),
            avatar: Math.max(availableHeight - avatarHeight, 0),
        },
        chatSpace,
        drag: {
            originX,
            minVideoWidth: MIN_VIDEO_WIDTH,
            maxVideoWidth,
            basisWidth,
            handleTop: actionsBar + videoHeight / 2 - 16,
        },
    }
}

export const dragDivision = (
    pageX: number,
    { originX, minVideoWidth, maxVideoWidth, basisWidth }: LandscapeDrag,
) =>
    getPercentage(
        Math.max(Math.min(pageX - originX, maxVideoWidth), minVideoWidth),
        basisWidth,
    )

export const calcPortraitGeometry = (
    width: number,
    height: number,
): PortraitGeometry => {
    const videoHeight = calcHeight(width)
    const zoomedVideoHeight = calcZoomedVideoHeight(height, width)
    return {
        width,
        videoHeight,
        zoomedVideoHeight,
        zoomedVideoWidth: calcWidth(zoomedVideoHeight),
        space: {
            normal: Math.max(height - videoHeight, 0),
            zoomed: Math.max(height - zoomedVideoHeight, 0),
            none: height,
        },
    }
}

export const calcTheaterGeometry = (
    width: number,
    height: number,
): TheaterGeometry => {
    const videoHeight = calcHeight(width)
    return {
        videoWidth: width,
        videoHeight,
        heightBelowVideo: Math.max(height - videoHeight, 0),
        // a tall video is cropped below the room; the chat stays inside it
        chatSpace: Math.min(videoHeight, height),
    }
}

export const calcMobileTheaterGeometry = (
    height: number,
): MobileTheaterGeometry => ({
    videoWidth: calcWidth(height),
    videoHeight: height,
    chatSpace: height,
})

export const calcFullWindowGeometry = (
    windowWidth: number,
    windowHeight: number,
): FullWindowGeometry => {
    const fittedWidth = calcWidth(windowHeight)
    const pillarboxed = fittedWidth <= windowWidth
    const videoWidth = pillarboxed ? fittedWidth : windowWidth
    const videoHeight = pillarboxed ? windowHeight : calcHeight(windowWidth)
    const leftover = pillarboxed
        ? windowWidth - videoWidth
        : windowHeight - videoHeight
    return {
        videoWidth,
        videoHeight,
        pillarboxed,
        verticalActionBar: fittedWidth < windowWidth,
        spacer: Math.ceil(leftover / 2),
        chatSpace: windowHeight,
    }
}

export const calcBasicPmGeometry = (
    width: number,
    height: number,
): BasicPmGeometry => ({
    space: height,
    // matches the offline header's own room-container query
    compactHeader: width < APP_BREAKPOINTS.md,
})

export const calcRoomGeometry = (inputs: RoomGeometryInputs): RoomGeometry => {
    const {
        roomView,
        viewMode,
        width,
        height,
        windowWidth,
        windowHeight,
        roomX,
        division,
    } = inputs
    const split = calcPmSplit(inputs)
    const roomWidth = split ? split.roomWidth : width
    return {
        split,
        landscape: calcLandscapeGeometry({
            roomView,
            width: roomWidth,
            height,
            layoutWidth: split ? split.layoutWidth : roomWidth,
            division,
            originX: roomX + width - roomWidth + ROOM_BORDER_WIDTH,
        }),
        portrait: calcPortraitGeometry(roomWidth, height),
        theater: calcTheaterGeometry(width, height),
        mobileTheater: calcMobileTheaterGeometry(height),
        fullWindow: calcFullWindowGeometry(windowWidth, windowHeight),
        basicPm: calcBasicPmGeometry(
            viewMode === ViewMode.FullWindow ? windowWidth : width,
            height,
        ),
    }
}
